import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { COUNTRY_DIAL_CODES, normalizePhone, type Country } from "@nh/shared";
import { TextField, type TextFieldProps } from "./TextField";

/**
 * Normaliza a E.164 en el momento en que react-hook-form LEE el campo, así
 * que lo que se valida y lo que se envía ya es `+52...`. El API no adivina
 * formatos y desde aquí no se le manda nada que tenga que adivinar.
 *
 * Si no se puede interpretar, se conserva lo que la persona escribió: así
 * el esquema del contrato falla y su mensaje ("Ese teléfono no parece
 * válido") aparece EN EL CAMPO del teléfono, que es donde se corrige.
 * Devolver `""` en cambio haría creer que el campo está vacío.
 */
function toE164OrRaw(value: unknown, dialCode: string): string {
  const raw = typeof value === "string" ? value : "";
  return normalizePhone(raw, dialCode) ?? raw;
}

export interface PhoneFieldProps extends Omit<TextFieldProps, "name"> {
  /** Por defecto `"phone"`. */
  name?: string;
  /**
   * De qué país es este teléfono. Decide el prefijo con el que se
   * completan los diez dígitos nacionales que casi todo el mundo escribe.
   *
   * Por defecto México, que es donde opera el negocio. Existe porque el
   * campo normalizaba SIEMPRE con `+52`: quien elegía Estados Unidos y
   * escribía sus diez dígitos acababa registrado con un teléfono mexicano
   * que no es suyo, y nada en la pantalla lo delataba.
   */
  country?: Country;
}

/**
 * Teléfono conectado, con la normalización a E.164 DENTRO.
 *
 * Antes el `setValueAs` vivía suelto en `RegisterPage`: cada pantalla nueva
 * que pidiera un teléfono tenía que acordarse de copiarlo, y olvidarlo no
 * rompía nada visible — solo mandaba `"55 1234 5678"` al API. Ahora la
 * regla viaja con el campo.
 */
export function PhoneField({
  name = "phone",
  country = "MX",
  registerOptions,
  ...rest
}: PhoneFieldProps) {
  const dialCode = COUNTRY_DIAL_CODES[country];
  useRedialOnCountryChange(name, dialCode);

  return (
    <TextField
      label="Teléfono"
      placeholder="55 1234 5678"
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      {...rest}
      name={name}
      // `setValueAs` primero: quien pase `registerOptions` puede añadir
      // reglas sin perder la normalización, y sustituirla si de verdad
      // quiere otro formato.
      registerOptions={{
        setValueAs: (value: unknown) => toE164OrRaw(value, dialCode),
        ...registerOptions,
      }}
    />
  );
}

/**
 * Reescribe el prefijo cuando el país cambia DESPUÉS de escribir el número.
 *
 * `setValueAs` solo corre al teclear, así que sin esto el orden importaba:
 * quien escribía el teléfono, seguía al paso siguiente y luego volvía a
 * corregir su país se quedaba con el `+52` de antes guardado y con la
 * pantalla diciendo "Estados Unidos". Un desacuerdo invisible entre lo que
 * se ve y lo que se envía es exactamente el fallo que nadie reporta.
 *
 * Solo toca los números que ESTE campo compuso —el prefijo anterior
 * seguido de diez dígitos nacionales— y deja intacto cualquier otro. El
 * caso ambiguo (alguien que escribió `+52 ...` a mano y luego dice estar en
 * Estados Unidos) se resuelve a favor del país recién elegido: es el dato
 * más reciente que dio la persona.
 */
function useRedialOnCountryChange(name: string, dialCode: string) {
  const { getValues, setValue } = useFormContext();
  const previous = useRef(dialCode);

  useEffect(() => {
    const before = previous.current;
    previous.current = dialCode;
    if (before === dialCode) return;

    const current: unknown = getValues(name);
    if (typeof current !== "string" || !current.startsWith(before)) return;

    const national = current.slice(before.length);
    if (!/^\d{10}$/.test(national)) return;

    setValue(name, `${dialCode}${national}`);
  }, [dialCode, getValues, name, setValue]);
}
